import { useQuery } from "@tanstack/react-query";

import { CardGrid } from "@bagel-web/components";

import ComponentsCard from "./agentConfig/ComponentsCard";
import PresentationCard from "./agentConfig/PresentationCard";
import RoleCard from "./agentConfig/RoleCard";
import ServicesCard from "./agentConfig/ServicesCard";
import { agentLlmService, agentLlmServiceName } from "./playgroundDraft";
import { organizationServiceQueries } from "../queries";
import { ServiceParamValue } from "../service/ServiceParamValueField";
import {
  CommonOrganizationService,
  fetchOrganizationServicesCallback,
  PlaygroundAgentConfig,
  PlaygroundComponentConfig,
  PlaygroundRoleConfig,
} from "../types";

// A freshly selected service's params start from their declared defaults.
const serviceParamDefaults = (service: CommonOrganizationService | undefined) =>
  Object.fromEntries(
    Object.entries(service?.allowed_params ?? {})
      .filter(([, schema]) => schema.default !== undefined)
      .map(([name, schema]) => [name, schema.default])
  );

// A new component takes its type as its id, numbered past any collision.
const nextComponentId = (
  type: string,
  componentConfigs: Array<PlaygroundComponentConfig>
): string => {
  const taken = new Set(componentConfigs.map((component) => component.id));
  if (!taken.has(type)) {
    return type;
  }
  let ordinal = 2;
  while (taken.has(`${type}_${ordinal}`)) {
    ordinal += 1;
  }
  return `${type}_${ordinal}`;
};

/**
 * Step 2 — Agent Config.
 *
 * Renders the seed's `agent_config` block (create_agent_v1 API shapes) as
 * one card per section — Role, Services, Components, Presentation — and
 * owns the writes: each card hands back the edit it wants, and this step
 * folds it into the config for onAgentConfigChange. Component deletes and
 * renames cascade beyond the config block, so those go to the page instead.
 */
const PlaygroundAgentConfigStep = ({
  agentConfig,
  fetchOrganizationServices,
  onAgentConfigChange,
  onComponentDelete,
  onComponentRename,
}: {
  agentConfig: PlaygroundAgentConfig;
  fetchOrganizationServices: fetchOrganizationServicesCallback;
  onAgentConfigChange?: (agentConfig: PlaygroundAgentConfig) => void;
  // Deleting a component cascades beyond the config block (its seed
  // messages go with it), so the page handles it rather than
  // onAgentConfigChange. Renaming cascades the same way: the seed channel,
  // the messages riding it, and the presentation entries follow the id.
  onComponentDelete?: (component: PlaygroundComponentConfig) => void;
  onComponentRename?: (
    component: PlaygroundComponentConfig,
    nextId: string
  ) => void;
}) => {
  const roleConfig = agentConfig.role_config;
  const presentationConfig = agentConfig.presentation_config;
  const componentConfigs = agentConfig.component_configs || [];
  const serviceConfigs = agentConfig.service_configs || [];

  const updateRole = (patch: Partial<PlaygroundRoleConfig>) =>
    onAgentConfigChange?.({
      ...agentConfig,
      role_config: { ...roleConfig, ...patch },
    });

  // The org services back both the LLM dropdown (which loads through the
  // same query key) and the selected service's param schemas.
  const { data: organizationServices } = useQuery({
    queryKey: organizationServiceQueries.all(),
    queryFn: () => fetchOrganizationServices(),
    staleTime: Infinity,
  });

  // The entry's service_name is an org service; picking one rewrites the
  // entry.
  const llmService = agentLlmService(agentConfig);
  const llmServiceName = agentLlmServiceName(agentConfig);
  const selectedService = organizationServices?.find(
    (service) => service.name === llmServiceName
  );

  // Selecting a service replaces the entry wholesale: its params are
  // service-specific, so the old ones give way to the new service's
  // declared defaults.
  const updateAgentLlm = (serviceName: string) => {
    const agentLlm = agentConfig.agent_llm ?? "agent_llm";
    const entry = {
      id: llmService ? llmService.id : agentLlm,
      service_name: serviceName,
      ...serviceParamDefaults(
        organizationServices?.find((service) => service.name === serviceName)
      ),
    };
    onAgentConfigChange?.({
      ...agentConfig,
      agent_llm: agentLlm,
      service_configs: llmService
        ? serviceConfigs.map((service) =>
            service === llmService ? entry : service
          )
        : [...serviceConfigs, entry],
    });
  };

  const updateServiceParam = (name: string, value: ServiceParamValue) => {
    if (!llmService) return;
    const entry = { ...llmService };
    // An emptied number field parses to NaN: the param goes unset rather
    // than ride the payload unparseable.
    if (typeof value === "number" && Number.isNaN(value)) {
      delete entry[name];
    } else {
      entry[name] = value;
    }
    onAgentConfigChange?.({
      ...agentConfig,
      service_configs: serviceConfigs.map((service) =>
        service === llmService ? entry : service
      ),
    });
  };

  // A new component starts empty and presents its element — without a
  // presentation entry it would contribute nothing to the run.
  const addComponent = (type: string, element: string) => {
    const id = nextComponentId(type, componentConfigs);
    onAgentConfigChange?.({
      ...agentConfig,
      component_configs: [...componentConfigs, { id, type }],
      presentation_config: {
        ...presentationConfig,
        presentation_order: [
          ...(presentationConfig?.presentation_order ?? []),
          [id, element],
        ],
      },
    });
  };

  // An emptied field unsets the key so the config falls back to the
  // field's declared default — an empty-string delimiter or a NaN count
  // would corrupt the config rather than configure it.
  const updateComponentField = (
    component: PlaygroundComponentConfig,
    name: string,
    value: ServiceParamValue
  ) => {
    const entry = { ...component };
    if (value === "" || (typeof value === "number" && Number.isNaN(value))) {
      delete entry[name];
    } else {
      entry[name] = value;
    }
    onAgentConfigChange?.({
      ...agentConfig,
      component_configs: componentConfigs.map((candidate) =>
        candidate === component ? entry : candidate
      ),
    });
  };

  const setPresentationOrder = (order: Array<[string, string]>) =>
    onAgentConfigChange?.({
      ...agentConfig,
      presentation_config: {
        ...presentationConfig,
        presentation_order: order,
      },
    });

  return (
    <CardGrid maxColumns={1}>
      <RoleCard roleConfig={roleConfig} onChange={updateRole} />
      <ServicesCard
        llmService={llmService}
        llmServiceName={llmServiceName}
        selectedService={selectedService}
        fetchOrganizationServices={fetchOrganizationServices}
        onServiceChange={updateAgentLlm}
        onParamChange={updateServiceParam}
      />
      <ComponentsCard
        componentConfigs={componentConfigs}
        onAdd={addComponent}
        onFieldChange={updateComponentField}
        onDelete={onComponentDelete}
        onRename={onComponentRename}
      />
      <PresentationCard
        componentConfigs={componentConfigs}
        presentationOrder={presentationConfig?.presentation_order ?? []}
        onOrderChange={setPresentationOrder}
      />
    </CardGrid>
  );
};

export default PlaygroundAgentConfigStep;
